import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, UserCircle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYMENT_TYPE_META = {
  full_time:   { label: "Full-time",   cls: "bg-blue-100 text-blue-700" },
  part_time:   { label: "Part-time",   cls: "bg-amber-100 text-amber-700" },
  contractor:  { label: "Contractor",  cls: "bg-purple-100 text-purple-700" },
};

const STATUS_META = {
  active:   { label: "Active",   cls: "bg-green-100 text-green-700" },
  inactive: { label: "Inactive", cls: "bg-slate-100 text-slate-500" },
};

function StatusBadge({ value, meta }) {
  const m = meta[value] || { label: value, cls: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

export default function EmployeeList() {
  const navigate = useNavigate();
  const { agent, getAuthHeaders } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (search.trim()) params.set("search", search.trim());
      const res = await axios.get(`${API}/employees?${params}`, { headers: getAuthHeaders() });
      setEmployees(res.data || []);
    } catch {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  if (agent?.role !== "admin") {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64 text-slate-500 text-sm">
          Access restricted to administrators.
        </div>
      </DashboardLayout>
    );
  }

  const handleSearchKey = (e) => { if (e.key === "Enter") load(); };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Employees</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {employees.length} record{employees.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => navigate("/employees/new")}
              className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Plus className="w-4 h-4" />
              Add Employee
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email or job title…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleSearchKey}
              className="pl-9"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center h-40 text-slate-400 text-sm">
            Loading…
          </div>
        ) : employees.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400 gap-2">
            <UserCircle className="w-10 h-10" />
            <p className="text-sm">No employees found.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Job Title</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Payroll Email</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Start Date</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp, idx) => (
                    <tr
                      key={emp.id}
                      className={`border-b border-slate-100 last:border-0 hover:bg-indigo-50/40 cursor-pointer transition-colors ${
                        idx % 2 === 0 ? "" : "bg-slate-50/30"
                      }`}
                      onClick={() => navigate(`/employees/${emp.id}`)}
                    >
                      <td className="px-4 py-3 font-medium text-slate-800">{emp.full_name}</td>
                      <td className="px-4 py-3 text-slate-600">{emp.job_title || "—"}</td>
                      <td className="px-4 py-3">
                        <StatusBadge value={emp.employment_type} meta={EMPLOYMENT_TYPE_META} />
                      </td>
                      <td className="px-4 py-3 text-slate-600">{emp.payroll_email}</td>
                      <td className="px-4 py-3 text-slate-500">{emp.start_date || "—"}</td>
                      <td className="px-4 py-3">
                        <StatusBadge value={emp.status} meta={STATUS_META} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
